/**
 * How a push payload reads as a notification. Shared between the service
 * worker, which shows it, and the tests, which check the wording without a
 * worker.
 */

import { attachmentsLabel } from "@/chat/format"
import type { RoomKind, User } from "@/chat/protocol"

/** What the server sends; mirrored from its `push.ts`. */
export type PushPayload = {
  roomId: string
  roomKind: RoomKind
  roomName: string
  author: User
  text: string
  /** Pictures and videos together. */
  attachments: number
  /** How many of `attachments` are videos; absent from an older server. */
  videos?: number
  /** Not a message: the author has started a voice chat in the room. */
  voice?: boolean
  at: number
}

export type NotificationText = { title: string; body: string }

/**
 * A direct room is named "a & b" on the server, so the author is the better
 * title there; a group shows its own name and the author leads the body.
 */
export function describeNotification(payload: PushPayload): NotificationText {
  const text = payload.text.trim()
  const media = attachmentsLabel(payload.attachments, payload.videos ?? 0)
  const content = payload.voice
    ? "📞 Голосовой чат — присоединяйтесь"
    : text && media
      ? `${media} · ${text}`
      : text || media

  if (payload.roomKind === "direct") {
    return { title: payload.author.name, body: content }
  }

  return { title: payload.roomName, body: `${payload.author.name}: ${content}` }
}

export function isPushPayload(value: unknown): value is PushPayload {
  if (typeof value !== "object" || value === null) return false
  const v = value as Record<string, unknown>
  const author = v.author as Record<string, unknown> | undefined
  return (
    typeof v.roomId === "string" &&
    (v.roomKind === "group" || v.roomKind === "direct") &&
    typeof v.roomName === "string" &&
    typeof author === "object" &&
    author !== null &&
    typeof author.name === "string" &&
    typeof v.text === "string" &&
    typeof v.attachments === "number"
  )
}
